/**
 * "Take me there" navigation targets for validation findings.
 *
 * PAGE-LEVEL only (this slice): each check id maps to the destination SCREEN.
 * Item-level focus — opening *this* specific form / DD entry / visit — is a
 * later slice; every workspace screen currently reads only `projectId` from the
 * URL, so a deep-link to a specific item does not exist yet.
 *
 * Routes are the child-route segments under /project/:projectId (see main.tsx).
 * Mapping follows docs/validation-ui-design.md §B.2, with two deliberate tweaks
 * noted inline (L3, L4) where the fix lives on a different screen than the copy.
 */

export interface NavTarget {
  /** Child route under /project/:projectId. */
  route: string;
  /** Button label — what the user will see. */
  label: string;
  /**
   * Optional query string that drives a sub-view on the destination screen
   * (e.g. `view=schedule` selects the CRF builder's Form Assignments tab). The
   * screen reads it on load and falls back to its default when absent, so
   * normal navigation is unaffected. Established for schedule-of-events this
   * slice; other destinations can adopt the same `view=` param later.
   */
  query?: string;
}

const STUDY_SETUP: NavTarget = { route: "study-design", label: "Open study setup" };
const STUDY_DESIGN: NavTarget = { route: "study-design", label: "Open study design" };
const FORMS: NavTarget = { route: "crf-builder", label: "Open the forms", query: "view=forms" };
// The CRF builder's Form Assignments sub-view (workspaceView === 'schedule') is
// where a visit / schedule-of-events finding is resolved.
const SCHEDULE: NavTarget = { route: "crf-builder", label: "Open Form Assignments", query: "view=schedule" };
const MATRIX: NavTarget = { route: "crf-builder", label: "Open Form Assignments", query: "view=schedule" };
const DICTIONARY: NavTarget = { route: "data-management", label: "Open the data dictionary" };
const EDIT_CHECKS: NavTarget = { route: "data-management", label: "Open the edit checks" };
const SDTM: NavTarget = { route: "data-management", label: "Open the SDTM mapping" };
const GUIDE: NavTarget = { route: "data-management", label: "Open the completion guide" };

/** Explicit per-check overrides where the prefix rule isn't right. */
const OVERRIDES: Record<string, NavTarget> = {
  S1: STUDY_SETUP,
  A1: STUDY_DESIGN, // arms live in study design
  A2: STUDY_DESIGN,
  A3: SCHEDULE, // visits / schedule of events live in the CRF builder
  B1: MATRIX,
  B2: MATRIX,
  B3: MATRIX,
  B4: MATRIX,
  B5: MATRIX,
  B6: FORMS,
  // W1-W14: the generated forms against the protocol reading. Every fix is in the CRF Builder.
  W1: FORMS, W2: FORMS, W3: FORMS, W4: FORMS, W5: FORMS, W6: FORMS, W7: FORMS,
  W8: FORMS, W9: FORMS, W10: FORMS, W11: FORMS, W12: FORMS, W13: FORMS, W14: FORMS,
  L1: GUIDE,
  L2: GUIDE,
  // L3's remedy is to set the EDC system in study setup, not to open the guide.
  L3: STUDY_SETUP,
  // L4's schedule of events lives in the CRF builder.
  L4: SCHEDULE,
};

/** Destination screen for a check id. Always returns a target (never null). */
export function navTargetFor(checkId: string): NavTarget {
  if (OVERRIDES[checkId]) return OVERRIDES[checkId];
  const c = checkId[0];
  if (c === "S") return STUDY_SETUP;
  if (c === "A" || c === "B") return SCHEDULE;
  if (c === "C" || c === "D" || c === "E") return FORMS;
  if (c === "F" || c === "G" || c === "H" || c === "I") return DICTIONARY;
  if (c === "J") return EDIT_CHECKS;
  if (c === "K") return SDTM;
  if (c === "L") return GUIDE;
  return FORMS;
}
